import { Alert, Link } from 'omakase';
import { DisplayDate } from '@tock/time';
import { LocalDate } from '@js-joda/core';
import { consumer, core } from '@tock/coretypes';
import { css } from '@emotion/react';
import { pluralize } from '@tock/shared/utils/pluralize';
import { useBusinessCalendar } from 'hooks/calendar/useBusinessCalendar';
import _ from 'lodash';

interface Props {
  waitlistUrl: string;
  business: consumer.IBusinessConfig;
  onNavigateToNextDate(date: string, experience: core.ITicketType): void;
  ticketTypeIdOverride?: number;
}

const ExperienceModalStatus: React.FunctionComponent<Props> = ({
  waitlistUrl,
  business,
  onNavigateToNextDate,
  ticketTypeIdOverride,
}) => {
  const {
    ticketTypePartySizeDates,
    ticketTypeDates,
    search: {
      params: { date: selectedDate, partySize: selectedSize },
    },
    selectedTicketType: experience,
    getTicketTypeIsFullySoldOut,
  } = useBusinessCalendar({ ticketTypeId: ticketTypeIdOverride });
  const availableDates = ticketTypePartySizeDates?.availableDates;
  const offeredDates = ticketTypePartySizeDates?.offeredDates;

  const notOfferedForSize = _.isEmpty(offeredDates);
  const notOfferedForSizeDate = !offeredDates?.[selectedDate];
  const notOfferedForDate = !ticketTypeDates?.[selectedDate];

  const soldOutForSize = offeredDates?.[selectedDate] && !availableDates?.[selectedDate];
  const isFullySoldOut = getTicketTypeIsFullySoldOut();

  const nextAvailableDate = _.head(_.sortBy(_.keys(_.pickBy(availableDates))));

  const handleNavigateToNextExperience = () => {
    const nextAvailableDate = _.head(_.sortBy(_.keys(_.pickBy(availableDates))));
    if (nextAvailableDate) {
      return experience && onNavigateToNextDate(nextAvailableDate, experience);
    }
    return null;
  };

  if (!selectedSize || !selectedDate) {
    return null;
  }

  const showWaitlist = business.showWaitlist;

  if (notOfferedForSize) {
    return (
      <Alert
        css={css`
          margin-top: 3rem;
        `}
        severity="warning"
        icon={false}
        action={showWaitlist ? <Link href={waitlistUrl}>Notify</Link> : undefined}
      >
        {experience ? experience.name : 'This experience'} is not offered for{' '}
        {pluralize(selectedSize, 'guest')}. Try another party size
        {showWaitlist ? <span> or join the Notify list.</span> : '.'}
      </Alert>
    );
  }

  if (selectedDate < LocalDate.now().toString()) {
    return (
      <Alert
        css={css`
          margin-top: 3rem;
        `}
        severity="warning"
        icon={false}
      >
        {business.name} reservations are no longer available for{' '}
        <DisplayDate dateValue={selectedDate} displayMonthAs="short" showYear={false} />
      </Alert>
    );
  }

  if (selectedDate > (_.max(Object.entries(offeredDates || {}).map((entry) => entry[0])) || '')) {
    return (
      <Alert
        css={css`
          margin-top: 3rem;
        `}
        severity="warning"
        icon={false}
      >
        {business.name} has not opened reservations for{' '}
        <DisplayDate dateValue={selectedDate} displayMonthAs="short" showYear={false} />
      </Alert>
    );
  }

  if (isFullySoldOut) {
    return (
      <Alert
        css={css`
          margin-top: 3rem;
        `}
        severity="warning"
        icon={false}
        action={showWaitlist ? <Link href={waitlistUrl}>Notify</Link> : undefined}
      >
        {experience?.name} is sold out.
      </Alert>
    );
  }

  if (soldOutForSize) {
    return (
      <Alert
        css={css`
          margin-top: 3rem;
        `}
        severity="warning"
        icon={false}
        action={
          nextAvailableDate ? (
            <Link
              onClick={(event) => {
                event.preventDefault();
                handleNavigateToNextExperience();
              }}
            >
              View reservation
            </Link>
          ) : undefined
        }
      >
        Sold out for parties of {selectedSize}.
        {nextAvailableDate && (
          <span>
            <span>
              &nbsp;on&nbsp;
              <DisplayDate dateValue={selectedDate} displayMonthAs="short" showYear={false} />.
            </span>
            <span>&nbsp;The next available date is on&nbsp;</span>
            <span>
              <DisplayDate dateValue={nextAvailableDate} displayMonthAs="short" showYear={false} />.
            </span>
          </span>
        )}
      </Alert>
    );
  }

  if (notOfferedForDate) {
    return (
      <Alert
        css={css`
          margin-top: 3rem;
        `}
        severity="warning"
        icon={false}
        action={showWaitlist ? <Link href={waitlistUrl}>Notify</Link> : undefined}
      >
        {experience?.name} is not offered on{' '}
        <DisplayDate dateValue={selectedDate} displayMonthAs="short" showYear={false} />. Please try
        another date
        {showWaitlist ? <span> or join the Notify list.</span> : '.'}
      </Alert>
    );
  }

  if (notOfferedForSizeDate) {
    return (
      <Alert
        css={css`
          margin-top: 3rem;
        `}
        severity="warning"
        icon={false}
        action={showWaitlist ? <Link href={waitlistUrl}>Notify</Link> : undefined}
      >
        {experience?.name} is not offered on{' '}
        <DisplayDate dateValue={selectedDate} displayMonthAs="short" showYear={false} /> for{' '}
        {pluralize(selectedSize, 'guest')}. Try another date or party size
        {showWaitlist ? <span> or join the Notify list.</span> : '.'}
      </Alert>
    );
  }

  return null;
};

export default ExperienceModalStatus;
